import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getMemberList } from 'app/redux/store/features/members'
import MemberGridFilter from './components/MemberGridFilter'
import MemberGridData from './components/MemberGridData'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'

const MembersGrid = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [filter, setFilter] = useState({
        sort: 'FirstName',
        isDescending: false,
    })

    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25
    const { familyConfig } = useSelector((state) => state.families)

    useEffect(() => {
        setApiCallInProgress(true)
        if (familyConfig.familyId) {
            setFilter({
                familyId: familyConfig.familyId,
            })
            dispatch(getMemberList({ ...filter, familyId: familyConfig.familyId, skip: 0, take: pageSize }))
        }
    }, [familyConfig.familyId, dispatch])

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function userReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [memberGrid, setMemberGrid] = React.useReducer(
        userReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            currentfilter = { ...filter, ...currentfilter }
            dispatch(getMemberList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getMemberList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getMemberList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getMemberList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setMemberGrid({ type: 'set-grid-filter', payload: value })
        },
        [setMemberGrid]
    )

    const onAddMember = () => {
        navigate(`/registrations/families/member/add/${familyConfig.familyId}`, {
            state: { id: familyConfig.familyId },
        })
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setMemberGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setMemberGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        onClick={onRefresh}
                        fontSize={'large'}
                        sx={{
                            mb: -1.2,
                            ml: -2,
                            mr: 0.5,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Member List
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'primary.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddMember}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                memberGrid.filterIconColor && !memberGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!memberGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <MemberGridFilter
                    mutation={applyFilter}
                    display={memberGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <MemberGridData
                    onRefresh={onRefresh}
                    familyId={familyConfig.familyId}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    apiCallInProgress={apiCallInProgress}
                    setApiCallInProgress={setApiCallInProgress}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default MembersGrid
